// app-update.ts — pure logic for the in-app "a new version is available" prompt.
//
// Context: OTA JS bundles (Capgo) apply themselves silently on the next
// background→foreground, so those reach users with no prompt. What DOESN'T
// self-deliver is a new NATIVE build shipped through the App Store (e.g. build
// 18): the store has it, but the installed app gives no in-app nudge, so a user
// only discovers it if they happen to open the App Store. This module decides,
// from the running version and the App Store's current version, whether to show
// that nudge. Kept Deno-free and side-effect-free so vitest covers it directly;
// the network fetch + Capacitor calls live in useAppUpdate.
//
// iOS only for now — the App Store is the surface. Android would use the Play
// Store's in-app-update API instead (a separate follow-up).

// The App Store listing + the lookup endpoint. bundleId matches
// scripts/asc-review-watch.py (com.dashalign.app / app id 6775979399).
export const APP_STORE_BUNDLE_ID = 'com.dashalign.app';
export const APP_STORE_APP_ID = '6775979399';
export const APP_STORE_URL = `https://apps.apple.com/app/id${APP_STORE_APP_ID}`;
export const ITUNES_LOOKUP_URL = `https://itunes.apple.com/lookup?bundleId=${APP_STORE_BUNDLE_ID}&country=us`;

// localStorage key for the version the user last dismissed. Dismissing suppresses
// the prompt for THAT version only — the next release prompts again.
export const UPDATE_DISMISSED_KEY = 'dashalign:update-dismissed-version';

/**
 * Compare two dotted numeric version strings ("1.0.2" vs "1.0.10").
 * Returns -1 if a < b, 1 if a > b, 0 if equal. Non-numeric / missing parts are
 * treated as 0, and unequal lengths are padded ("1.0" === "1.0.0"), so the
 * comparison is component-wise numeric — NOT a string compare, which would order
 * "1.0.10" before "1.0.9".
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const pa = String(a ?? '').split('.');
  const pb = String(b ?? '').split('.');
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = parseInt(pa[i] ?? '0', 10) || 0;
    const nb = parseInt(pb[i] ?? '0', 10) || 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
}

/** True when the store's version is strictly newer than what's installed. */
export function isStoreUpdateAvailable(current: string, store: string): boolean {
  if (!current || !store) return false;
  return compareVersions(store, current) > 0;
}

/**
 * The full gate for showing the prompt: a newer store version exists AND the
 * user hasn't already dismissed that exact version. Dismissal is per-version so
 * one "not now" doesn't mute every future release.
 */
export function shouldShowUpdatePrompt(args: {
  current: string;
  store: string;
  dismissedVersion?: string | null;
}): boolean {
  const { current, store, dismissedVersion } = args;
  if (!isStoreUpdateAvailable(current, store)) return false;
  return store !== dismissedVersion;
}

/** Pull the App Store version string out of an iTunes Lookup response. */
export function parseStoreVersion(lookupJson: unknown): string | null {
  const results = (lookupJson as { results?: Array<{ version?: unknown }> })?.results;
  const v = Array.isArray(results) && results.length > 0 ? results[0]?.version : undefined;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}
